/**
 * The shape the UI renders, independent of which backend produced it.
 *
 * Lives in its own module so that both the legacy client and the fundraising
 * client can produce it without importing each other.
 */

export interface CharityEventPackage {
    packageId: string;
    title: string;
    name: string;
    /** Dollars. The fundraising API speaks cents and converts at its boundary. */
    cost: number;
    description: string;
    /**
     * Undefined when availability was not fetched — the events list does not
     * need it, and asking for it there would mean one extra request per event.
     * The registration form, which does need it, is reached through the detail
     * view, where it is always populated.
     */
    remaining?: number;
    maxPackageCount: number;
}

export interface CharityEvent {
    eventId: string;
    eventTitle: string;
    description: string;
    eventDate: string;
    image?: string;
    location: string;
    address: string;
    registrationStart: string;
    registrationEnd: string;
    formImage?: string;
    packages: CharityEventPackage[];
}

export interface AttendeeDetails {
    firstName: string;
    lastName?: string;
    email: string;
    phone?: string;
    shirtSize?: string;
}

export interface RegistrationRequest {
    eventId: string;
    packages: {
        packageId: string;
        /** The form's Select yields a string; the new API wants an integer. */
        qty: string;
        details: AttendeeDetails[];
    }[];
}

/**
 * What comes back from starting a registration.
 *
 * The legacy API returns only a client secret; the fundraising API also returns
 * the hold and when it expires. Both clients produce this shape so the form does
 * not care which one served it — `holdId` and `expiresAt` are optional because
 * the legacy backend genuinely cannot supply them.
 */
export interface PlacedHold {
    clientSecret: string;
    holdId?: string;
    expiresAt?: string;
    amountCents?: number;
}

/** Why a registration was refused, in a form the UI can branch on. */
export type RegistrationRefusalCode =
    | 'sold_out'
    | 'already_registered'
    | 'registration_closed'
    | 'too_many'
    | 'unknown_package'
    | 'event_not_found'
    | 'refused';

export class RegistrationRefused extends Error {
    constructor(
        readonly code: RegistrationRefusalCode,
        readonly detail: string,
    ) {
        super(detail);
        this.name = 'RegistrationRefused';
    }
}

const MESSAGES: Record<RegistrationRefusalCode, string> = {
    sold_out: 'Those spots were just taken. Please refresh the page and try again.',
    already_registered: 'That email address is already registered for this event.',
    registration_closed: 'Registration for this event is closed.',
    too_many: 'That is more than one registration may take of that package.',
    unknown_package: 'That package is no longer available for this event.',
    event_not_found: 'We could not find that event.',
    refused: 'We could not hold those spots. Please try again.',
};

/** The copy a visitor sees. Chosen from the code, never from the server's prose. */
export const registrationRefusalMessage = (code: RegistrationRefusalCode): string =>
    MESSAGES[code] ?? MESSAGES.refused;
